<template>
  <b-card no-body class="border mb-0">
    <b-card-header class="py-2 px-3 bg-light">
      <h6 class="mb-0 font-weight-bold">
        <i class="fas fa-filter mr-1 text-primary"></i>
        Clinic patient filters
        <b-badge :variant="contextBadgeVariant" pill class="ml-2">{{ contextLabel }}</b-badge>
      </h6>
    </b-card-header>
    <b-card-body class="p-3">
      <b-row>
        <b-col cols="12" md="4" class="mb-2">
          <label class="font-weight-bold small mb-1">Name</label>
          <b-form-input v-model.trim="localFilters.name" size="sm" @keyup.enter="search" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <label class="font-weight-bold small mb-1">Email</label>
          <b-form-input v-model.trim="localFilters.email" size="sm" @keyup.enter="search" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <label class="font-weight-bold small mb-1">Phone</label>
          <b-form-input v-model="localFilters.phone" size="sm" @keyup.enter="search" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <label class="font-weight-bold small mb-1">City</label>
          <treeselect
            v-model="localFilters.cities_id"
            :async="true"
            :load-options="searchState"
            :normalizer="normalizer"
            placeholder="City"
            size="sm" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <label class="font-weight-bold small mb-1">Zip code</label>
          <b-form-input v-model.trim="localFilters.zip_codes_id" size="sm" @keyup.enter="search" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <label class="font-weight-bold small mb-1">DOB</label>
          <flat-pickr
            v-model="localFilters.date_birth"
            :config="dateConfig"
            class="form-control form-control-sm"
            placeholder="MM-DD-YYYY" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <label class="font-weight-bold small mb-1">Created date</label>
          <flat-pickr
            v-model="localFilters.create"
            :config="dateConfig"
            class="form-control form-control-sm"
            placeholder="MM-DD-YYYY" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <label class="font-weight-bold small mb-1">Gender</label>
          <treeselect
            v-model="localFilters.gender"
            :options="genderOptions"
            :normalizer="normalizer"
            placeholder="Gender" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <label class="font-weight-bold small mb-1">Status</label>
          <treeselect
            v-model="localFilters.status"
            :options="statusOptions"
            :normalizer="normalizer"
            placeholder="Status" />
        </b-col>
        <b-col cols="12" md="8" class="mb-2">
          <label class="font-weight-bold small mb-1">Facility</label>
          <treeselect
            v-model="localFilters.facilities_id"
            :async="true"
            :load-options="searchFacility"
            :default-options="true"
            :normalizer="normalizer"
            multiple
            placeholder="Facilities" />
        </b-col>
        <b-col cols="12" class="d-flex justify-content-end mt-1">
          <b-button variant="outline-secondary" size="sm" class="mr-2" @click="clear">
            Clear
          </b-button>
          <b-button variant="primary" size="sm" :disabled="loading" @click="search">
            <SpinnerLoading v-if="loading" />
            <span v-else><i class="fas fa-search mr-1"></i> Search</span>
          </b-button>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BRow, BCol, BFormInput, BButton, BBadge } from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import flatPickr from 'vue-flatpickr-component'
import SpinnerLoading from '@/components/SpinnerLoading'
import {
  defaultClinicSearchFilters,
  getClinicCitiesApi,
  getClinicFacilitiesApi,
  isInsuranceContext,
} from '../clinicPatientBridge'

export default {
  name: 'ClinicPatientSearchPanel',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BBadge,
    Treeselect,
    flatPickr,
    SpinnerLoading,
  },
  props: {
    programContext: { type: String, default: 'normal' },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      localFilters: defaultClinicSearchFilters(),
      citySearchTimeout: null,
      genderOptions: [
        { name: 'Male', id: 'male' },
        { name: 'Female', id: 'female' },
      ],
      statusOptions: [
        { name: 'Active', id: 'true' },
        { name: 'Inactive', id: 'false' },
      ],
      dateConfig: { dateFormat: 'm-d-Y', allowInput: true },
      normalizer(node) {
        return { id: node.id, label: node.name }
      },
    }
  },
  computed: {
    contextLabel() {
      return isInsuranceContext(this.programContext) ? 'Insurance' : 'Regular'
    },
    contextBadgeVariant() {
      return isInsuranceContext(this.programContext) ? 'warning' : 'primary'
    },
  },
  methods: {
    search() {
      this.$emit('search', JSON.parse(JSON.stringify(this.localFilters)))
    },
    clear() {
      this.localFilters = defaultClinicSearchFilters()
      this.$emit('clear')
    },
    searchState({ action, searchQuery, callback }) {
      if (action !== 'ASYNC_SEARCH') return callback(null, [])
      clearTimeout(this.citySearchTimeout)
      this.citySearchTimeout = setTimeout(() => {
        getClinicCitiesApi(this.programContext)
          .citiesFilterFilters({ name: searchQuery })
          .then(response => {
            const data = response?.data ?? response ?? []
            callback(null, Array.isArray(data) ? data : [])
          })
          .catch(() => callback(null, []))
      }, 350)
    },
    searchFacility({ action, searchQuery, callback }) {
      if (action !== 'ASYNC_SEARCH') return callback(null, [])
      getClinicFacilitiesApi(this.programContext)
        .facilityFilter(30, { name: searchQuery })
        .then(({ data }) => callback(null, data || []))
        .catch(() => callback(null, []))
    },
  },
}
</script>

<style scoped>
::v-deep .vue-treeselect__control {
  min-height: 31px;
  font-size: 0.875rem;
}
</style>
